import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useToast } from '../../hooks/useToast';
import Toast from '../../components/Toast';
import Spinner from '../../components/Spinner';
import ConfirmationModal from '../../components/ConfirmationModal';
import './AdminImageCleanup.css';

function AdminImageCleanup() {
  const [products, setProducts] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [images, setImages] = useState([]);
  const [selectedImages, setSelectedImages] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [confirmPrune, setConfirmPrune] = useState(false);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const { toast, showToast, closeToast } = useToast();

  useEffect(() => {
    fetchProducts();
  }, []);

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('adminToken')}`
  });

  const fetchProducts = async () => {
    try {
      const response = await axios.get('/api/admin/products/image-heavy', {
        headers: getAuthHeaders()
      });

      setProducts(response.data);
      setLoading(false);

      if (response.data.length > 0 && !selectedProductId) {
        fetchProductImages(response.data[0].id);
      }
    } catch (error) {
      setLoading(false);
      showToast('Failed to load products: ' + (error.response?.data?.error || error.message), 'error');
    }
  };

  const fetchProductImages = async (productId) => {
    setSelectedProductId(productId);
    setImagesLoading(true);
    setSelectedImages(new Set());

    try {
      const response = await axios.get(`/api/admin/products/${productId}/images`, {
        headers: getAuthHeaders()
      });

      setSelectedProduct(response.data.product);
      setImages(response.data.images);
    } catch (error) {
      showToast('Failed to load images: ' + (error.response?.data?.error || error.message), 'error');
    } finally {
      setImagesLoading(false);
    }
  };

  const toggleImage = (imageId) => {
    setSelectedImages(prev => {
      const next = new Set(prev);

      if (next.has(imageId)) {
        next.delete(imageId);
        return next;
      }

      if (next.size >= 10) {
        showToast('You can keep up to 10 images for a product', 'warning');
        return next;
      }

      next.add(imageId);
      return next;
    });
  };

  const openPruneConfirm = () => {
    if (!selectedProduct || selectedImages.size === 0) {
      showToast('Select at least one image to keep', 'warning');
      return;
    }

    setConfirmPrune(true);
  };

  const pruneImages = async () => {
    if (!selectedProduct) return;

    setSaving(true);

    try {
      const response = await axios.put(
        `/api/admin/products/${selectedProduct.id}/images/prune`,
        { keepImageIds: [...selectedImages] },
        { headers: getAuthHeaders() }
      );

      showToast(
        `Saved ${response.data.remaining} image${response.data.remaining === 1 ? '' : 's'} and deleted ${response.data.deleted}`,
        'success'
      );
      setConfirmPrune(false);
      await fetchProducts();
      await fetchProductImages(selectedProduct.id);
    } catch (error) {
      showToast('Failed to prune images: ' + (error.response?.data?.error || error.message), 'error');
    } finally {
      setSaving(false);
    }
  };

  const deleteProduct = async (productId) => {
    if (!window.confirm('Delete this product? This cannot be undone.')) return;

    try {
      await axios.delete(`/api/admin/products/${productId}`, {
        headers: getAuthHeaders()
      });

      showToast('Product deleted', 'success');
      setSelectedProduct(null);
      setImages([]);
      setSelectedImages(new Set());
      fetchProducts();
    } catch (error) {
      showToast('Failed to delete product: ' + (error.response?.data?.error || error.message), 'error');
    }
  };

  if (loading) return <Spinner text="Loading image-heavy products..." />;

  const imagesToDelete = Math.max(images.length - selectedImages.size, 0);

  return (
    <div className="admin-image-cleanup">
      {toast && <Toast message={toast.message} type={toast.type} onClose={closeToast} />}

      <div className="image-cleanup-header">
        <div>
          <h1>Image Cleanup</h1>
          <p>Products with more than 10 photos. Select the images to keep, then save to delete the rest.</p>
        </div>
        <div className="image-cleanup-count">{products.length} products</div>
      </div>

      <div className="image-cleanup-layout">
        <aside className="image-heavy-list">
          {products.map(product => (
            <button
              key={product.id}
              type="button"
              className={`image-heavy-product ${selectedProductId === product.id ? 'active' : ''}`}
              onClick={() => fetchProductImages(product.id)}
            >
              <span>{product.name}</span>
              <strong>{product.image_count}</strong>
            </button>
          ))}
        </aside>

        <section className="image-cleanup-panel">
          {!selectedProduct ? (
            <div className="empty-image-panel">No product selected.</div>
          ) : (
            <>
              <div className="selected-product-bar">
                <div>
                  <h2>{selectedProduct.name}</h2>
                  <p>
                    {selectedImages.size} selected to keep
                    {' '}· {imagesToDelete} will be deleted
                    {' '}· Max 10
                  </p>
                </div>
                <div className="selected-product-actions">
                  <button type="button" onClick={() => navigate(`/admin/products/${selectedProduct.id}/edit`)}>
                    Edit Product
                  </button>
                  <button type="button" className="danger" onClick={() => deleteProduct(selectedProduct.id)}>
                    Delete Product
                  </button>
                  <button type="button" className="primary" onClick={openPruneConfirm}>
                    Save Selected Images
                  </button>
                </div>
              </div>

              {imagesLoading ? (
                <Spinner text="Loading product images..." />
              ) : (
                <div className="cleanup-image-grid">
                  {images.map(image => (
                    <button
                      key={image.id}
                      type="button"
                      className={`cleanup-image-card ${selectedImages.has(image.id) ? 'selected' : ''}`}
                      onClick={() => toggleImage(image.id)}
                    >
                      <img src={image.image_url} alt="" />
                      <span>{selectedImages.has(image.id) ? 'Keep' : 'Delete unless selected'}</span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </section>
      </div>

      <ConfirmationModal
        isOpen={confirmPrune}
        onClose={() => setConfirmPrune(false)}
        title="Delete Unselected Images?"
        actions={
          <>
            <button className="btn btn-secondary" onClick={() => setConfirmPrune(false)} disabled={saving}>
              Cancel
            </button>
            <button className="btn btn-danger" onClick={pruneImages} disabled={saving}>
              {saving ? 'Deleting...' : 'Confirm Delete'}
            </button>
          </>
        }
      >
        <p>
          This will keep <strong>{selectedImages.size}</strong> selected image{selectedImages.size === 1 ? '' : 's'} for
          <strong> {selectedProduct?.name}</strong>.
        </p>
        <p>
          All other images for this product, currently <strong>{imagesToDelete}</strong>, will be permanently deleted.
        </p>
      </ConfirmationModal>
    </div>
  );
}

export default AdminImageCleanup;
